import React, { useEffect } from 'react';
import get from 'lodash.get';
import { SelectionStyle, SelectionType } from '@philips-internal/oswald-ui-constants';
import { ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { ClickEvent, SelectEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { classNames, getLostProps, isFunction, isValidWidth, propagateProps, resolveHSLColor, trigger } from '@philips-internal/oswald-ui-utils';
import { BaseCardListOrientation } from '../base-card-list/base-card-list-constants.js';
import { Checkbox } from '../checkbox/checkbox.js';
import { useOptionalLegends } from '../legends/legends-context.js';
import { RadioButton } from '../radio-group/radio-button.js';
import { HORIZONTAL_DECK_CARDS_GAP } from './card-constants.js';
import { CardProperties } from './card-types.js';
import './card.css';
const CardContentContainer = {
    acceptedTypes: [
        ComponentType.BEHAVIOR,
        ComponentType.DATA_DISPLAY,
        ComponentType.LAYOUT,
        ComponentType.LIST,
    ],
    name: '',
};
function CardSuperscription(props) {
    const { superscription = [], superscriptionEntry } = props;
    const context = useOptionalLegends();
    const propValue = get(props, String(superscriptionEntry));
    const superscriptionItem = superscription.find(item => item.value === propValue);
    useEffect(() => {
        if (!context || !superscription.length) {
            return;
        }
        superscription.forEach((item) => {
            context.addItem(item);
        });
        return () => {
            superscription.forEach((item) => {
                context.removeItem(item.value);
            });
        };
    }, [superscription, context?.addItem, context?.removeItem]);
    if (!superscriptionItem) {
        return null;
    }
    return (React.createElement("div", { className: "c-card__superscription", style: { backgroundColor: resolveHSLColor(superscriptionItem.color) } }));
}
function CardCheckbox(props) {
    const { disabled, onSelect, selectable, selected, selectionType } = props;
    if (!selectable || selectionType !== SelectionType.MULTIPLE) {
        return null;
    }
    return (React.createElement(Checkbox, { className: "c-card__checkbox", enabled: !disabled, name: "checkbox", onChange: ({ value }) => onSelect(value), uuid: "card-checkbox-uuid", value: Boolean(selected) }));
}
function CardRadioButton(props) {
    const { disabled, onSelect, selectable, selected, selectionStyle, selectionType } = props;
    if (!selectable || selectionType !== SelectionType.SINGLE || selectionStyle !== SelectionStyle.RADIO) {
        return null;
    }
    return (React.createElement(RadioButton, { checked: Boolean(selected), className: "c-card__radio-button", enabled: !disabled, label: "", onChange: ({ value }) => onSelect(value), readOnly: true, uuid: "card-radio-button-uuid", value: "" }));
}
function Card(props) {
    const { children, className, data, disabled, onClick, onContextMenuClick, onDoubleClick, onSelect, orientation = CardProperties.orientation.defaultValue, padding = CardProperties.padding.defaultValue, role, selectable, selected, selectionStyle, selectionType, transparent = CardProperties.transparent.defaultValue, uuid = CardProperties.uuid.defaultValue, width = CardProperties.width.defaultValue, } = props;
    const toggleSelect = () => {
        if (!disabled && isFunction(onSelect)) {
            onSelect(new SelectEvent({
                selected: !selected,
                value: uuid,
            }));
        }
    };
    const onCardClick = (event) => {
        if (disabled) {
            return;
        }
        if (selectable && selectionType !== SelectionType.NONE) {
            toggleSelect();
        }
        if (isFunction(onClick)) {
            onClick(new ClickEvent({ value: event }));
        }
    };
    const cardClassNames = classNames({
        'c-card': true,
        'c-card--disabled': Boolean(disabled),
        'c-card--selectable': Boolean(selectable),
        'c-card--selected': Boolean(selected),
        'c-card--transparent': transparent,
        [`u-grid-col-${width}`]: isValidWidth(width),
        [`${className}`]: Boolean(className),
    });
    const lostProps = getLostProps(CardProperties, props);
    const widthValue = isValidWidth(width) ? width : CardProperties.width.defaultValue;
    const cardWidth = orientation === BaseCardListOrientation.HORIZONTAL
        ? `calc(calc((calc(${widthValue} * 100%)) / var(--columns)) - ${HORIZONTAL_DECK_CARDS_GAP})`
        : 'auto';
    return (React.createElement("div", { className: cardClassNames, onClick: onCardClick, onContextMenu: trigger(onContextMenuClick, new ClickEvent()), onDoubleClick: trigger(onDoubleClick, new ClickEvent()), role: role, style: {
            maxWidth: cardWidth,
            minWidth: cardWidth,
        } },
        React.createElement(CardSuperscription, { ...props }),
        React.createElement("div", { className: "c-card__content-wrapper", style: { padding } },
            React.createElement(Container, { acceptedTypes: CardContentContainer.acceptedTypes, className: "c-card__content", uuid: uuid }, propagateProps(children, { ...lostProps, data, disabled })),
            React.createElement(CardCheckbox, { disabled: disabled, onSelect: toggleSelect, selectable: selectable, selected: selected, selectionType: selectionType }),
            React.createElement(CardRadioButton, { disabled: disabled, onSelect: toggleSelect, selectable: selectable, selected: selected, selectionStyle: selectionStyle, selectionType: selectionType }))));
}
export { Card, CardContentContainer, };
//# sourceMappingURL=card.js.map